import { ReactElement, ReactNode, useCallback, useMemo, useState } from 'react'

import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  rectIntersection,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
	SortableContext,
	arrayMove,
	useSortable,
	sortableKeyboardCoordinates,
	verticalListSortingStrategy
} from '@dnd-kit/sortable'
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from '@dnd-kit/modifiers'
import { CSS } from '@dnd-kit/utilities'

import { List, ListProps, ListItem, Paper, Stack } from '@mui/material'
import { DragIndicator } from '@mui/icons-material'

export type Sortable<T> = T & { id: number, draggable?: boolean }

type useSortableListProps<T> = {
	list: T[]
	toSortable: (data: T) => Sortable<T>
	onChangeList?: (list: Sortable<T>[]) => void
}
export const useSortableList = <T,>({
	list,
	toSortable,
	onChangeList
}: useSortableListProps<T>) => {
  const [sortableList, setSortableList] = useState<Sortable<T>[]>(list.map(toSortable))

	const addRecord = useCallback(
		(data: T) => {
			const changedList = [...sortableList].concat([toSortable(data)])
			setSortableList(changedList)
			if (onChangeList) onChangeList(changedList)
			return changedList
		},
		[sortableList, toSortable, onChangeList]
	)
	const editRecord = useCallback(
		(data: Sortable<T>) => {
			const changedList = sortableList.map(item => item.id === data.id ? data : item)
			setSortableList(changedList)
			if (onChangeList) onChangeList(changedList)
			return changedList
		},
		[sortableList, onChangeList]
	)
	const removeRecord = useCallback(
		(id: number) => {
			const changedList = sortableList.filter(item => item.id !== id)
			setSortableList(changedList)
			if (onChangeList) onChangeList(changedList)
			return changedList
		},
		[sortableList, onChangeList]
	)
	const onDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event
      if (over) {
				const changedList = arrayMove(
          sortableList,
          sortableList.findIndex(item => item.id === active.id),
          sortableList.findIndex(item => item.id === over.id)
        )
        setSortableList(changedList)
				if (onChangeList) onChangeList(changedList)
      }
    },
    [sortableList, setSortableList, onChangeList]
  )
	return useMemo(() => ({
		sortableList,
		setSortableList,
		addRecord,
		editRecord,
		removeRecord,
		onDragEnd
	}), [
		sortableList,
		setSortableList,
		addRecord,
		editRecord,
		removeRecord,
		onDragEnd
	])
}

type SortableItemRowProps<T> = {
	hasGrab: boolean
  data: Sortable<T>
	children: ReactNode
}
const SortableItemRow = <T,>({
	hasGrab,
  data,
	children
}: SortableItemRowProps<T>) => {
  const {
    attributes, listeners,
    setNodeRef,
		setActivatorNodeRef,
		transform,
    isDragging,
  } = useSortable({
		id: data.id
	})
  return hasGrab ? (
    <ListItem
			ref={setNodeRef}
      style={{
        opacity: isDragging ? 0.3 : 1,
				transform: CSS.Transform.toString(transform)
      }}
			sx={{ alignItems: 'stretch' }}
			disablePadding disableGutters dense
    >
			<Stack
				ref={setActivatorNodeRef}
				direction="column"
				justifyContent="center"
				paddingX={0.5}
				sx={{ cursor: "grab" }}
				{...attributes}
				{...listeners}
			>
				<DragIndicator fontSize='small' color="action"/>
			</Stack>

      {children}
    </ListItem>
	) : (
    <ListItem
      ref={setNodeRef}
      style={{
        opacity: isDragging ? 0.3 : 1,
				transform: CSS.Transform.toString(transform),
        cursor: "grab"
      }}
      disablePadding disableGutters dense
      {...attributes}
      {...listeners}
    >
      {children}
    </ListItem>
  )
}
const DisabledSortableItemRow = <T,>({
	hasGrab,
	children
}: SortableItemRowProps<T>) => {
  return hasGrab ? (
    <ListItem
			sx={{ alignItems: 'stretch' }}
			disablePadding disableGutters dense
    >
			<Stack
				direction="column"
				justifyContent="center"
				paddingX={0.5}
			>
				<DragIndicator fontSize='small' color="disabled"/>
			</Stack>
      {children}
    </ListItem>
	) : (
    <ListItem disablePadding disableGutters dense>
      {children}
    </ListItem>
  )
}

type SortableListProps<T> = {
	listProps?: ListProps
	renderItem: (data: T) => ReactElement
	hasGrab?: boolean
} & ReturnType<typeof useSortableList<T>>
export const SortableList = <T,>({
	listProps,
	renderItem,
	hasGrab,
	sortableList,
	onDragEnd,
}: SortableListProps<T>) => {
	const [activeId, setActiveId] = useState<number | null>(null);
	const sensors = useSensors(
		useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
	)
	const activeItem = useMemo(
		() => activeId ? sortableList.find(item => item.id === activeId) : undefined,
		[activeId, sortableList]
	)
	return (
		<DndContext
			sensors={sensors}
			collisionDetection={rectIntersection}
			onDragStart={(event) => {
				setActiveId(event.active.id as number);
			}}
			onDragEnd={(event) => {
				setActiveId(null)
				onDragEnd(event)
			}}
			modifiers={[restrictToParentElement, restrictToVerticalAxis]}
		>
			<List {...listProps}>
				<SortableContext
					items={sortableList}
					strategy={verticalListSortingStrategy}
				>
					{sortableList.map((data, i) => data.draggable === false ? (
						<DisabledSortableItemRow
							key={`sortableitem-${i}`}
							data={data}
							hasGrab={hasGrab === true}
						>
							{renderItem(data)}
						</DisabledSortableItemRow>
					) : (
						<SortableItemRow
							key={`sortableitem-${i}`}
							data={data}
							hasGrab={hasGrab === true}
						>
							{renderItem(data)}
						</SortableItemRow>
					))}
				</SortableContext>
			</List>

			{activeItem && (
				<DragOverlay>
					<Paper elevation={3} square>
						<ListItem disablePadding disableGutters dense style={{ cursor: "grabbing" }}>
							{hasGrab ? (
								<Stack
									direction="column"
									justifyContent="center"
									paddingX={0.5}
									sx={{ cursor: "grabbing" }}
								>
									<DragIndicator fontSize='small' color='action'/>
								</Stack>
							) : <></>}
							{renderItem(activeItem)}
						</ListItem>
					</Paper>
				</DragOverlay>
			)}
		</DndContext>
	)
}